declare global {
  interface Window {
    __googleMapsPromise?: Promise<typeof google>;
  }
}

export const loadGoogleMaps = (
  apiKey: string,
  libraries: string[] = ['places']
): Promise<typeof google> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps can only be loaded in the browser.'));
  }

  if (!apiKey) {
    return Promise.reject(new Error('Google Maps API key is missing.'));
  }

  if (window.google?.maps?.places) {
    return Promise.resolve(window.google);
  }

  if (window.__googleMapsPromise) {
    return window.__googleMapsPromise;
  }

  window.__googleMapsPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[data-google-maps]');

    if (existingScript) {
      existingScript.addEventListener('load', () => {
        if (window.google?.maps) {
          resolve(window.google);
        } else {
          reject(new Error('Google Maps failed to initialize after script load.'));
        }
      });

      existingScript.addEventListener('error', (event) => {
        reject(new Error(`Google Maps script failed to load: ${event}`));
      });
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries.join(',')}`;
    script.async = true;
    script.defer = true;
    script.dataset.googleMaps = 'true';

    script.addEventListener('load', () => {
      if (window.google?.maps) {
        resolve(window.google);
      } else {
        reject(new Error('Google Maps failed to initialize after script load.'));
      }
    });

    script.addEventListener('error', (event) => {
      script.remove();
      window.__googleMapsPromise = undefined;
      reject(new Error(`Google Maps script failed to load: ${event}`));
    });

    document.head.appendChild(script);
  });

  return window.__googleMapsPromise;
};

